import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { ArrowLeft, Save, X, CreditCard } from 'lucide-react';
import Select from 'react-select';
import toast from 'react-hot-toast';
import { subscriptionsAPI, companiesAPI } from '../../services/api';

const SubscriptionForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);
  
  const [selectedCompany, setSelectedCompany] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    control
  } = useForm({
    defaultValues: {
      plan_name: '',
      plan_type: 'basic',
      duration_months: 1,
      features: '',
      status: 'active',
      company_uuid: '',
      discount: 0,
      description: '',
      coverage_type: 'company_wide'
    }
  });

  // Fetch companies for dropdown
  const { data: companiesData } = useQuery(
    'companies-active',
    () => companiesAPI.getAll({ is_active: true, limit: 1000 }),
    {
      onError: (error) => {
        console.error('Failed to load companies:', error);
        toast.error('Failed to load companies');
      }
    }
  );

  // Fetch subscription data for editing
  const { data: subscriptionData, isLoading: isLoadingSubscription } = useQuery(
    ['subscription', id],
    () => subscriptionsAPI.getById(id),
    {
      enabled: isEditing,
      onSuccess: (response) => {
        console.log('Subscription form - API response:', response);
        
        // Handle different response structures
        let subscription = null;
        if (response.data?.data) {
          subscription = response.data.data;
        } else if (response.data) {
          subscription = response.data;
        }

        console.log('Subscription form - extracted data:', subscription);
        
        if (subscription) {
          // Parse features if it's a JSON string
          let parsedFeatures = '';
          if (subscription.features) {
            try {
              const featuresArray = JSON.parse(subscription.features);
              parsedFeatures = Array.isArray(featuresArray) ? featuresArray.join('\n') : subscription.features;
            } catch (e) {
              parsedFeatures = subscription.features;
            }
          }

          const formData = {
            plan_name: subscription.plan_name || '',
            plan_type: subscription.plan_type || 'basic',
            duration_months: subscription.duration_months || 1,
            features: parsedFeatures,
            status: subscription.status || 'active',
            company_uuid: subscription.company_uuid || '',
            discount: subscription.discount || 0,
            description: subscription.description || '',
          };

          console.log('Subscription form - form data to set:', formData);
          reset(formData);
          
          // Set selected company if exists
          if (subscription.company_uuid && companiesData?.data?.data?.companies) {
            const company = companiesData.data.data.companies.find(c => c.uuid === subscription.company_uuid);
            if (company) {
              setSelectedCompany({
                value: company.uuid,
                label: company.name
              });
            }
          }
        }
      },
      onError: (error) => {
        toast.error('Failed to load subscription data');
        navigate('/subscriptions');
      }
    }
  );

  // Handle company selection change
  const handleCompanyChange = (selectedOption) => {
    setSelectedCompany(selectedOption);
    setValue('company_uuid', selectedOption?.value || '');
  };

  const mutation = useMutation(
    async (data) => {
      // Process features - convert string to array
      console.log('Raw form data:', data);
      console.log('Selected company:', selectedCompany);
      
      const processedData = {
        ...data,
        duration_months: parseFloat(data.duration_months),
        features: data.features ? data.features.split('\n').filter(f => f.trim()) : [],
        company_uuid: selectedCompany?.value || null,
        coverage_type: 'company_wide',
        available_frequencies: data.available_frequencies || []
      };
      
      console.log('Submitting subscription data:', processedData);
      console.log('JSON.stringify:', JSON.stringify(processedData, null, 2));
      
      if (isEditing) {
        return subscriptionsAPI.update(id, processedData);
      } else {
        return subscriptionsAPI.create(processedData);
      }
    },
    {
      onSuccess: () => {
        toast.success(isEditing ? 'Subscription updated successfully!' : 'Subscription created successfully!');
        queryClient.invalidateQueries('subscriptions');
        navigate('/subscriptions');
      },
      onError: (error) => {
        console.error('Mutation error:', error);
        toast.error(error.response?.data?.message || 'Operation failed');
      }
    }
  );

  const onSubmit = async (data) => {
    mutation.mutate(data);
  };

  const handleCancel = () => {
    navigate('/subscriptions');
  };

  if (isEditing && isLoadingSubscription) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={handleCancel}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <CreditCard className="h-6 w-6" />
                {isEditing ? 'Edit Subscription Plan' : 'Create New Subscription Plan'}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {isEditing ? 'Update subscription plan template for mobile app' : 'Create a subscription plan template that will be available in the mobile app'}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
            {/* Plan Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Plan Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('plan_name', { required: 'Plan name is required' })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter plan name"
              />
              {errors.plan_name && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.plan_name.message}</p>
              )}
            </div>

            {/* Company Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Company <span className="text-red-500">*</span>
              </label>
              <Select
                value={selectedCompany}
                onChange={handleCompanyChange}
                options={
                  companiesData?.data?.data?.companies?.map(company => ({
                    value: company.uuid,
                    label: company.name
                  })) || []
                }
                placeholder="Select a company for this subscription plan"
                isSearchable
                className="react-select-container"
                classNamePrefix="react-select"
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: state.isFocused ? '#6366f1' : '#d1d5db',
                    boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.2)' : 'none',
                    '&:hover': {
                      borderColor: state.isFocused ? '#6366f1' : '#d1d5db'
                    }
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isFocused ? '#6366f1' : 'transparent',
                    color: state.isFocused ? 'white' : 'inherit',
                    '&:active': {
                      backgroundColor: '#4f46e5'
                    }
                  })
                }}
              />

            </div>

            {/* Hidden subscription type - always company_wide */}
            <input type="hidden" {...register('coverage_type')} value="company_wide" />



            {/* Plan Preview */}
            {selectedCompany && (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">
                  📱 Mobile Preview
                </h4>
                <p className="text-sm text-green-700 dark:text-green-300">
                  Customers will see this plan when browsing subscription-eligible products from <strong>{selectedCompany.label}</strong>.
                </p>
              </div>
            )}

            {/* Plan Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Plan Type <span className="text-red-500">*</span>
              </label>
              <select
                {...register('plan_type', { required: 'Plan type is required' })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              >
                <option value="basic">Basic</option>
                <option value="premium">Premium</option>
                <option value="enterprise">Enterprise</option>
              </select>
              {errors.plan_type && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.plan_type.message}</p>
              )}
            </div>

            {/* Plan Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Plan Description
              </label>
              <textarea
                {...register('description')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter plan description (e.g., 'Fresh vegetables delivered weekly')"
                rows="3"
              />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Brief description of what this subscription plan offers
              </p>
            </div>

            {/* Duration and Discount */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Duration (Months) <span className="text-red-500">*</span>
                </label>
                <select
                  {...register('duration_months', { required: 'Duration is required' })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="0.5">15 Days</option>
                  <option value="1">1 Month</option>
                  <option value="3">3 Months</option>
                  <option value="6">6 Months</option>
                  <option value="12">12 Months</option>
                </select>
                {errors.duration_months && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.duration_months.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  💰 Subscription Discount (%)
                </label>
                <input
                  type="number"
                  {...register('discount_percentage', { 
                    min: { value: 0, message: 'Discount cannot be negative' },
                    max: { value: 100, message: 'Discount cannot exceed 100%' }
                  })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  placeholder="0"
                  min="0"
                  max="100"
                  step="0.5"
                />
                {errors.discount_percentage && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.discount_percentage.message}</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  📱 This discount will be applied to selected products in mobile
                </p>
              </div>
            </div>

            {/* Features */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Features
              </label>
              <textarea
                {...register('features')}
                rows={4}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter plan features (one per line)"
              />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Enter each feature on a new line
              </p>
            </div>

            {/* Available Delivery Frequencies */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                🚚 Available Delivery Frequencies <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'daily', label: 'Daily Delivery', desc: 'Every day delivery' },
                  { value: 'alternate_days', label: 'Alternate Days', desc: 'Every other day' },
                  { value: 'weekly', label: 'Weekly', desc: 'Once per week' },
                  { value: 'custom', label: 'Custom Schedule', desc: 'Customer sets days' }
                ].map(freq => (
                  <label key={freq.value} className="flex items-start space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition-all">
                    <input
                      type="checkbox"
                      value={freq.value}
                      {...register('available_frequencies', { 
                        required: 'Please select at least one delivery frequency' 
                      })}
                      className="mt-1 h-4 w-4 text-blue-600"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-white text-sm">
                        {freq.label}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {freq.desc}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.available_frequencies && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.available_frequencies.message}</p>
              )}
              <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  📱 <strong>Mobile:</strong> Customers will only see these frequency options when subscribing to this plan.
                </p>
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                {...register('status', { required: 'Status is required' })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              {errors.status && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.status.message}</p>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
              <button
                type="submit"
                disabled={mutation.isLoading}
                className="flex-1 sm:flex-none px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center gap-2"
              >
                {mutation.isLoading ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {isEditing ? 'Update Plan' : 'Create Plan'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionForm;